import Phaser from 'phaser';
import { getBeyDef, artBase } from '../game/stats.ts';
import {
  newRun,
  advanceRun,
  isChampion,
  type TournamentRun,
} from '../game/tournament.ts';
import { loadProgress, saveProgress } from '../game/storage.ts';
import { recordChampionship } from '../game/progress.ts';
import { WORLD } from '../world.ts';
import { backdropGlow } from './TitleScene.ts';

/** The tournament ladder: shows the run's bracket and drives progression. */
export class TournamentScene extends Phaser.Scene {
  constructor() {
    super('Tournament');
  }

  create(data: { matchResult?: boolean }): void {
    this.cameras.main.setBackgroundColor('#05060a');
    backdropGlow(this);

    let run = this.registry.get('run') as TournamentRun | undefined;
    if (!run) {
      // Shouldn't happen, but recover gracefully.
      this.scene.start('Title');
      return;
    }
    if (typeof data?.matchResult === 'boolean') {
      run = advanceRun(run, data.matchResult);
      this.registry.set('run', run);
    }

    this.add
      .text(WORLD.width / 2, 54, run.hard ? "CHAMPION'S GAUNTLET" : 'TOURNAMENT', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '46px',
        fontStyle: 'bold',
        color: run.hard ? '#ff8a8a' : '#ffffff',
        stroke: '#000',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    const player = getBeyDef(run.playerId);
    this.add.sprite(140, 150, `bey-${artBase(player)}-face`).setDisplaySize(96, 96);
    this.add
      .text(140, 212, player.name, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    this.add
      .text(140, 238, 'YOUR BEY', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '15px',
        color: '#8aa0c8',
      })
      .setOrigin(0.5);

    this.drawLadder(run);
    this.drawFooter(run);
  }

  private drawLadder(run: TournamentRun): void {
    const x = 360;
    const top = 130;
    const rowH = 110;
    run.stages.forEach((stage, i) => {
      const y = top + i * rowH;
      const opp = getBeyDef(stage.opponentId);
      const beaten = i < run.current;
      const isCurrent = i === run.current && run.alive && !isChampion(run);
      const alpha = beaten ? 0.55 : isCurrent ? 1 : 0.7;

      const panel = this.add.graphics();
      panel.fillStyle(isCurrent ? 0x1d2c44 : 0x121a2b, 0.9);
      panel.fillRoundedRect(x, y - 40, 560, 84, 12);
      if (isCurrent) {
        panel.lineStyle(3, 0xffd23b, 0.9);
        panel.strokeRoundedRect(x, y - 40, 560, 84, 12);
      }
      panel.setAlpha(alpha);

      this.add.sprite(x + 50, y, `bey-${opp.id}-face`).setDisplaySize(64, 64).setAlpha(alpha);

      this.add
        .text(x + 100, y - 18, `Round ${i + 1}: ${opp.name}`, {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: '#ffffff',
        })
        .setAlpha(alpha);

      const stars = Math.min(4, Math.max(1, Math.round(stage.difficulty * 4)));
      this.add
        .text(x + 100, y + 10, `Difficulty ${'★'.repeat(stars)}${'☆'.repeat(4 - stars)}`, {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '16px',
          color: '#ffd23b',
        })
        .setAlpha(alpha);

      const status = beaten ? '✓ WON' : isCurrent ? '▶ NEXT' : 'LOCKED';
      const statusColor = beaten ? '#7CFC9A' : isCurrent ? '#ffd23b' : '#56607a';
      this.add
        .text(x + 540, y, status, {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: statusColor,
        })
        .setOrigin(1, 0.5)
        .setAlpha(alpha);
    });
  }

  private drawFooter(run: TournamentRun): void {
    const y = WORLD.height - 56;
    const champion = isChampion(run);

    if (champion) {
      // Persist the title (once — the champion screen leads only to the title).
      saveProgress(recordChampionship(loadProgress(), run.playerId));
      this.banner(run.hard ? 'GAUNTLET CLEARED!' : 'CHAMPION!', '#ffd23b');
      this.prompt('ENTER  —  back to title', y);
      this.input.keyboard!.once('keydown-ENTER', () => {
        this.registry.remove('run');
        this.scene.start('Title');
      });
      return;
    }
    if (!run.alive) {
      this.banner('ELIMINATED', '#ff5b5b');
      this.prompt('ENTER  title        R  try again', y);
      this.input.keyboard!.once('keydown-ENTER', () => {
        this.registry.remove('run');
        this.scene.start('Title');
      });
      this.input.keyboard!.once('keydown-R', () => {
        this.registry.set('run', newRun(run.playerId));
        this.scene.start('Tournament', {});
      });
      return;
    }

    const stage = run.stages[run.current];
    const opp = getBeyDef(stage.opponentId);
    this.prompt(`ENTER  —  fight ${opp.name}`, y);
    this.input.keyboard!.once('keydown-ENTER', () => {
      this.scene.start('Battle', {
        playerId: run.playerId,
        opponentId: stage.opponentId,
        difficulty: stage.difficulty,
        mode: 'tournament',
      });
    });
  }

  private banner(text: string, color: string): void {
    this.add
      .text(WORLD.width / 2, WORLD.height - 110, text, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '56px',
        fontStyle: 'bold',
        color,
        stroke: '#000',
        strokeThickness: 8,
      })
      .setOrigin(0.5);
  }

  private prompt(text: string, y: number): void {
    const t = this.add
      .text(WORLD.width / 2, y, text, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '26px',
        color: '#cfe0ff',
      })
      .setOrigin(0.5);
    this.tweens.add({ targets: t, alpha: 0.35, duration: 700, yoyo: true, repeat: -1 });
  }
}
